'use client';

type SegmentedToggleOption<T extends string> = {
  value: T;
  label: string;
};

type SegmentedToggleProps<T extends string> = {
  value: T;
  options: SegmentedToggleOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
  size?: 'xs' | 'sm' | 'md';
  mobileFullWidth?: boolean;
};

export default function SegmentedToggle<T extends string>({
  value,
  options,
  onChange,
  disabled = false,
  className = '',
  size = 'sm',
  mobileFullWidth = false,
}: SegmentedToggleProps<T>) {
  const activeIndex = options.findIndex((option) => option.value === value);
  const sizeClasses = mobileFullWidth
    ? size === 'xs'
      ? 'h-[15px] px-2 text-[8px] leading-[15px] sm:min-w-[34px]'
      : size === 'sm'
        ? 'h-6 px-3.5 text-[10px] leading-6 sm:min-w-14'
        : 'h-8 px-5 text-sm leading-8 sm:min-w-20'
    : size === 'xs'
      ? 'h-[15px] min-w-[34px] px-2 text-[8px] leading-[15px]'
      : size === 'sm'
        ? 'h-6 min-w-14 px-3.5 text-[10px] leading-6'
        : 'h-8 min-w-20 px-5 text-sm leading-8';

  return (
    <div
      role='radiogroup'
      className={`${mobileFullWidth ? 'flex w-full sm:inline-flex sm:w-auto' : 'inline-flex'} items-stretch overflow-hidden rounded-full border border-emerald-500 bg-[#c7ccd4] ${className}`}
    >
      {options.map((option, index) => {
        const active = index === activeIndex;
        return (
          <button
            key={option.value}
            type='button'
            role='radio'
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`${mobileFullWidth ? 'basis-0 sm:basis-auto' : ''} flex-1 rounded-none text-center ${sizeClasses} font-bold uppercase tracking-wider transition-colors ${
              active
                ? 'bg-emerald-500 text-white shadow-sm'
                : 'bg-[#c7ccd4] text-[#5f6875]'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
